import { ChangeDetectionStrategy, Component, Input, input, output, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { WaterHotspotSaveRequest } from '@models/water-information';
import { TranslatePipe } from '@ngx-translate/core';
import { ImageFieldComponent } from '@shared/components/image-picker/image-field.component';
import { LocalizedTextEditorComponent } from '@shared/components/localized-text-editor/localized-text-editor.component';
import type { HotspotIntroductionForm } from '../water-information-form.types';

@Component({
  selector: 'app-water-information-hotspots-section',
  templateUrl: './water-information-hotspots-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    TranslatePipe,
    ImageFieldComponent,
    LocalizedTextEditorComponent,
  ],
})
export class WaterInformationHotspotsSectionComponent {
  @Input({ required: true }) introductionForm!: FormGroup<HotspotIntroductionForm>;

  readonly hotspots = input.required<WaterHotspotSaveRequest[]>();
  readonly hotspotsChange = output<WaterHotspotSaveRequest[]>();
  readonly hotspotImageSelected = output<{ key: string; file: File }>();
  readonly hotspotImageCleared = output<string>();

  readonly langs = ['nl', 'en', 'de', 'it', 'fr'];

  private readonly pendingImages = signal<Map<string, { name: string; url: string }>>(new Map());

  private getKey(hotspot: WaterHotspotSaveRequest): string | null {
    if (hotspot.id) return String(hotspot.id);
    if (hotspot.clientId) return hotspot.clientId;
    return null;
  }

  getPendingImageName(hotspot: WaterHotspotSaveRequest): string | null {
    const key = this.getKey(hotspot);
    return key ? (this.pendingImages().get(key)?.name ?? null) : null;
  }

  getPendingImageUrl(hotspot: WaterHotspotSaveRequest): string | null {
    const key = this.getKey(hotspot);
    return key ? (this.pendingImages().get(key)?.url ?? null) : null;
  }

  addHotspot(): void {
    this.hotspotsChange.emit([
      ...this.hotspots(),
      {
        id: null,
        clientId: crypto.randomUUID(),
        city: null,
        distanceToWater: null,
        textNl: null,
        textEn: null,
        textDe: null,
        textIt: null,
        textFr: null,
        imageName: null,
        imageUrl: null,
      },
    ]);
  }

  removeHotspot(index: number): void {
    const hotspot = this.hotspots()[index];
    const key = this.getKey(hotspot);
    if (key) {
      const pending = this.pendingImages().get(key);
      if (pending) {
        URL.revokeObjectURL(pending.url);
        this.pendingImages.update((m) => {
          const n = new Map(m);
          n.delete(key);
          return n;
        });
      }
      this.hotspotImageCleared.emit(key);
    }
    this.hotspotsChange.emit(this.hotspots().filter((_, i) => i !== index));
  }

  setField(index: number, field: keyof WaterHotspotSaveRequest, value: unknown): void {
    this.hotspotsChange.emit(this.hotspots().map((h, i) => (i === index ? { ...h, [field]: value } : h)));
  }

  setText(index: number, lang: string, value: string): void {
    const field = `text${lang.charAt(0).toUpperCase()}${lang.slice(1)}` as keyof WaterHotspotSaveRequest;
    this.hotspotsChange.emit(this.hotspots().map((h, i) => (i === index ? { ...h, [field]: value || null } : h)));
  }

  selectImage(index: number, file: File): void {
    const hotspot = this.hotspots()[index];
    const key = this.getKey(hotspot);
    if (!key) return;
    const existing = this.pendingImages().get(key);
    if (existing) URL.revokeObjectURL(existing.url);
    const url = URL.createObjectURL(file);
    this.pendingImages.update((m) => new Map(m).set(key, { name: file.name, url }));
    this.hotspotImageSelected.emit({ key, file });
  }

  clearImage(index: number): void {
    const hotspot = this.hotspots()[index];
    const key = this.getKey(hotspot);
    if (key && this.pendingImages().has(key)) {
      const pending = this.pendingImages().get(key);
      if (pending) URL.revokeObjectURL(pending.url);
      this.pendingImages.update((m) => {
        const n = new Map(m);
        n.delete(key);
        return n;
      });
      this.hotspotImageCleared.emit(key);
    } else {
      this.hotspotsChange.emit(this.hotspots().map((h, i) => (i === index ? { ...h, imageName: null } : h)));
    }
  }
}
